import type { FieldDefinition } from '@/api/types'
import Select from '@/components/ui/Select'
import CustomFileField from '@/components/CustomFileField'

const fieldCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

/** The currently-selected values of a multiselect field, tolerating a legacy scalar value. */
function asArray(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String)
  if (raw == null || raw === '') return []
  return [String(raw)]
}

/**
 * The editor for a single custom field, chosen by its `dataType`. Shared by the deal and contact
 * detail panels so both render (and store) every type the field admin can create the same way.
 * `onChange` receives the stored shape for that type: a string for text-like types, a number,
 * a boolean, a string[] for multiselect, or the file descriptor object for file uploads; an empty
 * value is passed as null so it clears rather than storing "".
 */
export default function CustomFieldInput({
  def,
  value,
  onChange,
}: {
  def: FieldDefinition
  value: unknown
  onChange: (value: unknown) => void
}) {
  const raw = value
  switch (def.dataType) {
    case 'boolean':
      return (
        <input
          type="checkbox"
          checked={raw === true}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4"
        />
      )
    case 'number':
      return (
        <input
          type="number"
          value={raw == null ? '' : String(raw)}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className={fieldCls}
        />
      )
    case 'date':
      return (
        <input
          type="date"
          value={raw == null ? '' : String(raw)}
          onChange={(e) => onChange(e.target.value || null)}
          className={fieldCls}
        />
      )
    case 'datetime':
      return (
        <input
          type="datetime-local"
          value={raw == null ? '' : String(raw)}
          onChange={(e) => onChange(e.target.value || null)}
          className={fieldCls}
        />
      )
    case 'select':
      return (
        <Select
          size="md"
          value={raw == null ? '' : String(raw)}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full"
        >
          <option value="">—</option>
          {def.options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label ?? o.value}
            </option>
          ))}
        </Select>
      )
    case 'radio':
      return (
        <div className="space-y-1.5">
          {def.options?.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm text-brand-dark">
              <input
                type="radio"
                name={`${def.id}-radio`}
                checked={String(raw ?? '') === o.value}
                onChange={() => onChange(o.value)}
                className="h-4 w-4"
              />
              {o.label ?? o.value}
            </label>
          ))}
        </div>
      )
    case 'multiselect': {
      const selected = asArray(raw)
      const toggle = (val: string, on: boolean) => {
        const next = on ? [...selected, val] : selected.filter((v) => v !== val)
        onChange(next.length > 0 ? next : null)
      }
      return (
        <div className="space-y-1.5">
          {def.options?.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm text-brand-dark">
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={(e) => toggle(o.value, e.target.checked)}
                className="h-4 w-4"
              />
              {o.label ?? o.value}
            </label>
          ))}
        </div>
      )
    }
    case 'multiline':
      return (
        <textarea
          rows={3}
          value={raw == null ? '' : String(raw)}
          onChange={(e) => onChange(e.target.value || null)}
          className={fieldCls}
        />
      )
    case 'url':
      return (
        <input
          type="url"
          inputMode="url"
          placeholder="https://…"
          value={raw == null ? '' : String(raw)}
          onChange={(e) => onChange(e.target.value || null)}
          className={fieldCls}
        />
      )
    case 'email':
      return (
        <input
          type="email"
          inputMode="email"
          placeholder="name@example.com"
          value={raw == null ? '' : String(raw)}
          onChange={(e) => onChange(e.target.value || null)}
          className={fieldCls}
        />
      )
    case 'phone':
      return (
        <input
          type="tel"
          inputMode="tel"
          value={raw == null ? '' : String(raw)}
          onChange={(e) => onChange(e.target.value || null)}
          className={fieldCls}
        />
      )
    case 'file':
      return <CustomFileField value={raw} onChange={onChange} />
    default:
      return (
        <input
          type="text"
          value={raw == null ? '' : String(raw)}
          onChange={(e) => onChange(e.target.value || null)}
          className={fieldCls}
        />
      )
  }
}
